import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Card, CardBody } from '@/shared/components/Card';
import Order, { AccessoryOrderStatus } from '@/models/order';
import User from '@/models/user';
import { useUpdateOrder, formatMoney } from '@/services/orders';
import { ORDER_STATUS_OPTIONS, orderStatusLabel } from '@/utils/order-status';
import { formatDate } from '@/utils/helpers';
import Field from './field';
import { errorBannerStyle } from '@/shared/dark-inline-styles';

interface ShipmentCardProps {
    orders: Order[];
    user: User;
    onSaved: () => void;
}

/**
 * Shipment, read from the user's real orders (spec v2 §04).
 *
 * Writes go through the EXISTING `PATCH /accessories/admin/orders/:id`
 * (spec v2 §05 #4 — "Do not add a parallel write path"), so an edit made here
 * and one made on the Orders screen are the same operation and cannot diverge.
 *
 * The address is NOT editable here: it is populated automatically from Stripe
 * Checkout and §00 rules out building an address form. It is shown, with its
 * provenance, and copyable for a shipping label.
 */
const ShipmentCard = ({ orders, user, onSaved }: ShipmentCardProps) => {
    // Orders arrive newest-first from the API. The newest is the one an admin
    // is almost always acting on, but every order stays visible and selectable
    // — a user legitimately holds several (§07 #5).
    const [selectedId, setSelectedId] = useState(orders[0]?.id ?? '');
    const selected = orders.find((o) => o.id === selectedId) ?? orders[0];

    const [status, setStatus] = useState<AccessoryOrderStatus | ''>('');
    const [carrier, setCarrier] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [trackingUrl, setTrackingUrl] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const updateOrder = useUpdateOrder();

    useEffect(() => {
        if (!selected) return;
        setStatus(selected.status);
        setCarrier(selected.carrier ?? '');
        setTrackingNumber(selected.trackingNumber ?? '');
        setTrackingUrl(selected.trackingUrl ?? '');
        setError(null);
    }, [selected]);

    const address = user.shippingAddress;
    const addressLines = address
        ? [address.line1, address.line2, [address.city, address.state, address.postal_code].filter(Boolean).join(', '), address.country]
              .filter(Boolean)
              .join('\n')
        : '';

    const handleCopy = async () => {
        if (!addressLines) return;
        await navigator.clipboard.writeText([user.shippingName, addressLines].filter(Boolean).join('\n'));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSave = async () => {
        if (!selected || !status) return;
        setError(null);
        const result = await updateOrder.mutateAsync({
            orderId: selected.id,
            // The STORED enum value, never the display label.
            status: status as AccessoryOrderStatus,
            carrier,
            trackingNumber,
            trackingUrl,
        });
        // `callApi` resolves rather than throws on an API error, so a failed
        // save would otherwise look identical to a successful one.
        if (result.status === 'error') {
            setError(result.error?.message ?? 'Failed to update the order.');
            return;
        }
        onSaved();
    };

    if (!orders.length) {
        return (
            <Card>
                <CardBody>
                    <h4 className="mb-3">Shipment</h4>
                    <p className="text-muted mb-0">This user has no orders.</p>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card>
            <CardBody>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <h4 className="mb-0">Shipment</h4>
                    {orders.length > 1 && (
                        <select
                            className="form-select"
                            style={{ width: 'auto' }}
                            value={selected?.id}
                            onChange={(e) => setSelectedId(e.target.value)}
                            aria-label="Select order"
                        >
                            {orders.map((o) => (
                                <option key={o.id} value={o.id}>
                                    {o.orderNumber} · {orderStatusLabel(o.status)} · {formatDate(o.created_at)}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {error && <div style={errorBannerStyle}>{error}</div>}

                <div className="row">
                    <div className="col-md-6">
                        <Field label="Order" value={selected?.orderNumber} />
                        <Field label="Placed" value={selected ? formatDate(selected.created_at) : undefined} />
                        <Field label="Total" value={selected ? formatMoney(selected.totalAmount, selected.currency) : undefined} />
                        <Field label="Items" value={(selected?.lineItems ?? []).map((li) => `${li.quantity}× ${li.name}`).join(', ')} />

                        <hr />
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-muted">Ship to</span>
                            <Button size="sm" variant="outline-secondary" onClick={handleCopy} disabled={!addressLines}>
                                {copied ? 'Copied' : 'Copy address'}
                            </Button>
                        </div>
                        {addressLines ? (
                            <>
                                <div style={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                                    {user.shippingName ? `${user.shippingName}\n` : ''}
                                    {addressLines}
                                </div>
                                <small className="text-muted">
                                    {user.addressUpdatedAt ? `Updated ${formatDate(user.addressUpdatedAt)}` : ''}
                                    {user.addressSource ? ` · ${user.addressSource}` : ''}
                                </small>
                            </>
                        ) : (
                            // A comped or off-platform order legitimately has no
                            // address — Stripe never collected one.
                            <span className="text-muted">No address on file.</span>
                        )}
                    </div>

                    <div className="col-md-6">
                        <div className="mb-3">
                            <label className="form-label" htmlFor="shipmentStatus">
                                Status
                            </label>
                            <select
                                id="shipmentStatus"
                                className="form-select"
                                value={status}
                                onChange={(e) => setStatus(e.target.value as AccessoryOrderStatus)}
                            >
                                {ORDER_STATUS_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-3">
                            <label className="form-label" htmlFor="carrier">
                                Carrier
                            </label>
                            <input id="carrier" className="form-control" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label" htmlFor="trackingNumber">
                                Tracking number
                            </label>
                            <input
                                id="trackingNumber"
                                className="form-control"
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label" htmlFor="trackingUrl">
                                Tracking link
                            </label>
                            <input id="trackingUrl" className="form-control" value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} />
                        </div>
                        <Button variant="primary" onClick={handleSave} disabled={updateOrder.isPending}>
                            {updateOrder.isPending ? 'Saving…' : 'Save shipment'}
                        </Button>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default ShipmentCard;
